import {
	IAuthResponse,
	IForgotPasswordFrom,
	ILoginForm,
	ISingUpForm,
	ITokens,
	IUser,
} from 'src/lib/schemas';
import http from './http';
const apiVersion = 'v1';
const authUrl = `/${apiVersion}/auth`;
const tokensKey = 'tokens';
const userKey = 'user';

interface IOtpResponse {
	message: string;
	requiresOtp: boolean;
	user?: IUser;
}

const login = async (payload: ILoginForm): Promise<IUser | null | IOtpResponse> => {
	try {
		const { data } = await http.post<IAuthResponse | IOtpResponse>(
			`${authUrl}/login`,
			payload,
		);

		// Check if OTP is required
		if ('requiresOtp' in data && data.requiresOtp) {
			return data;
		}

		// Standard login response
		const authData = data as IAuthResponse;
		setTokens(authData.tokens);
		setCurrentUser(authData.user);
		return authData.user;
	} catch (error: any) {
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw error;
		}
	}
};

const signup = async (payload: ISingUpForm): Promise<IUser | IOtpResponse> => {
	const { pricingId, ...bodyPayload } = payload;
	const url = pricingId
		? `${authUrl}/register?priceId=${pricingId}`
		: `${authUrl}/register`;
	const { data } = await http.post<IAuthResponse | IOtpResponse>(url, bodyPayload);

	// Check if OTP is required
	if ('requiresOtp' in data && data.requiresOtp) {
		return data;
	}

	return (data as IAuthResponse).user;
};

const sendOtp = async (phone: string, captchaToken?: string): Promise<{ message: string }> => {
	const { data } = await http.post<{ message: string }>(`${authUrl}/send-otp`, {
		phone,
		captchaToken,
	});
	return data;
};

const verifyOtp = async (phone: string, otp: string, appsumoCode?: string): Promise<IUser> => {
	const payload: { phone: string; otp: string; appsumoCode?: string } = { phone, otp };

	if (appsumoCode) {
		payload.appsumoCode = appsumoCode;
	}

	const { data } = await http.post<IAuthResponse>(`${authUrl}/verify-otp`, payload);
	setTokens(data.tokens);
	setCurrentUser(data.user);
	return data.user;
};

interface IInitiateAuthResponse {
	user: IUser | null;
	isNewUser: boolean;
	requiresOtp: boolean;
	requiresEmailVerification: boolean;
	message: string;
}

const initiateAuth = async (identifier: string, timezone?: string, captchaToken?: string, campaignIndex?: number): Promise<IInitiateAuthResponse> => {
	const body: Record<string, any> = {
		identifier,
		timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
		captchaToken,
	};
	if (campaignIndex !== undefined) {
		body.campaignIndex = campaignIndex;
	}
	const { data } = await http.post<IInitiateAuthResponse>(`${authUrl}/initiate`, body);
	return data;
};

const setTokens = (tokens: ITokens) => {
	localStorage.setItem(tokensKey, JSON.stringify(tokens));
};
const getTokens = (): ITokens | null => {
	const tokens = localStorage.getItem(tokensKey);
	if (!tokens) {
		return null;
	}
	const parsedTokens = JSON.parse(tokens) as ITokens;
	return parsedTokens;
};
const setCurrentUser = (user: IUser) => {
	localStorage.setItem(userKey, JSON.stringify(user));
};
const getCurrentUser = (): IUser | null => {
	const user = localStorage.getItem(userKey);
	if (!user) {
		return null;
	}
	const parsedUser = JSON.parse(user) as IUser;
	return parsedUser;
};
const isAuthenticated = () => {
	const token = localStorage.getItem(tokensKey);
	return !!token;
};
const forgotPassword = async (payload: IForgotPasswordFrom) => {
	await http.post(`${authUrl}/forgot-password`, payload);
};
const resetPassword = async (token: string, password: string) => {
	const { data } = await http.post(`${authUrl}/reset-password?token=${token}`, {
		password,
	});
	setTokens(data.tokens);
	setCurrentUser(data.user);
};
const acceptInvitation = async (token: string, password?: string, otp?: string) => {
	const payload: any = {};
	if (password) payload.password = password;
	if (otp) payload.otp = otp;

	const { data } = await http.post<IAuthResponse>(
		`${authUrl}/accept-invitation/${token}`,
		payload,
	);
	setTokens(data.tokens);
	setCurrentUser(data.user);
};
const verifyEmail = async (
	token: string,
	priceId?: string,
	storageData?: Record<string, string>,
	campaignIndex?: number,
): Promise<IAuthResponse> => {
	let url = `${authUrl}/verify-email?token=${token}`;
	if (priceId) {
		url += `&priceId=${priceId}`;
	}
	if (campaignIndex !== undefined) {
		url += `&campaign=${campaignIndex}`;
	}
	const payload = storageData ? { storageData } : {};
	const { data } = await http.post<IAuthResponse>(url, payload);
	setTokens(data.tokens);
	setCurrentUser(data.user);
	return data;
};
const sendVerificationEmail = async () => {
	await http.post(`${authUrl}/send-verification-email`);
};
const logout = async () => {
	cleanupSession();
};
const cleanupSession = () => {
	localStorage.removeItem(tokensKey);
	localStorage.removeItem(userKey);
};
const refreshToken = async () => {
	const tokens = getTokens();
	if (!tokens) {
		console.log('🔐 No tokens found for refresh');
		return null;
	}

	try {
		const { data } = await http.post<IAuthResponse>(`${authUrl}/refresh-tokens`, {
			refreshToken: tokens.refresh.token,
		});
		setTokens(data.tokens);
		setCurrentUser(data.user);
		console.log('🔐 Token refresh successful');
		return data;
	} catch (error: any) {
		console.error('🔐 Token refresh failed:', error.message);
		// Clean up session on refresh failure
		cleanupSession();
		throw error;
	}
};

const fetchCurrentUser = async (): Promise<IUser | null> => {
	try {
		const { data } = await http.get<IUser>('/v1/users/me');
		setCurrentUser(data);
		return data;
	} catch (error: any) {
		console.error('Failed to fetch current user:', error.message);
		return null;
	}
};

export default {
	login,
	signup,
	logout,
	cleanupSession,
	getTokens,
	setTokens,
	isAuthenticated,
	getCurrentUser,
	setCurrentUser,
	fetchCurrentUser,
	forgotPassword,
	resetPassword,
	verifyEmail,
	sendVerificationEmail,
	refreshToken,
	acceptInvitation,
	sendOtp,
	verifyOtp,
	initiateAuth,
};
